import { arrayMove } from "@dnd-kit/sortable";
import {
  groupSessionBlocks,
  sessionBlockContaining,
  type SessionBlock,
  type SessionSection,
} from "@trainrr/api/workout-sections";

type SessionGroup<T extends { section_id?: string | null } = { section_id?: string | null }> =
  T[];

export const sessionBlockKey = <T,>(block: SessionBlock<T>) =>
  block.kind === "section"
    ? `${block.section.id}:${block.groups[0]?.groupIndex ?? 0}`
    : `loose:${block.groups[0]?.groupIndex ?? 0}`;

export const sectionGroupRanges = <T extends { section_id?: string | null }>(
  groups: SessionGroup<T>[],
  sections: SessionSection[] | undefined,
) =>
  groupSessionBlocks(groups, sections).flatMap((block) => {
    if (block.kind !== "section" || block.groups.length === 0) {
      return [];
    }
    const indexes = block.groups.map((item) => item.groupIndex);
    return [
      {
        min: Math.min(...indexes),
        max: Math.max(...indexes),
      },
    ];
  });

export const wrapSectionIdForGroup = <T extends { section_id?: string | null }>(
  groups: SessionGroup<T>[],
  sections: SessionSection[] | undefined,
  groupIndex: number,
) => {
  const block = sessionBlockContaining(groups, sections, groupIndex);
  if (block?.kind === "section") {
    return block.section.id;
  }
  return (
    block?.groups[0]?.group[0]?.section_id ??
    groups[groupIndex]?.[0]?.section_id ??
    null
  );
};

export const clampLoggerDropIndex = <T extends { section_id?: string | null }>(
  fromIndex: number,
  toIndex: number,
  groups: SessionGroup<T>[],
  sections: SessionSection[] | undefined,
) => {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= groups.length ||
    toIndex >= groups.length
  ) {
    return fromIndex;
  }

  const block = sessionBlockContaining(groups, sections, fromIndex);
  if (block?.kind === "section") {
    const indexes = block.groups.map((item) => item.groupIndex);
    const min = Math.min(...indexes);
    const max = Math.max(...indexes);
    return Math.min(max, Math.max(min, toIndex));
  }

  let target = toIndex;
  for (const { min, max } of sectionGroupRanges(groups, sections)) {
    if (target < min || target > max) continue;
    if (fromIndex >= min && fromIndex <= max) continue;
    const mid = (min + max) / 2;
    if (fromIndex < min) {
      target = target <= mid ? Math.max(0, min - 1) : max;
    } else {
      target = target <= mid ? min : Math.min(groups.length - 1, max + 1);
    }
  }
  return target;
};

export const moveLoggerGroups = <T extends { section_id?: string | null }>(
  groups: SessionGroup<T>[],
  sections: SessionSection[] | undefined,
  fromIndex: number,
  toIndex: number,
): SessionGroup<T>[] => {
  const clamped = clampLoggerDropIndex(fromIndex, toIndex, groups, sections);
  if (clamped === fromIndex) return groups;
  const sectionId = wrapSectionIdForGroup(groups, sections, fromIndex);
  const moved = arrayMove(groups, fromIndex, clamped);
  const group = moved[clamped];
  if (!group) return moved;
  moved[clamped] = group.map((exercise) => ({
    ...exercise,
    section_id: sectionId ?? exercise.section_id ?? null,
  }));
  return moved;
};
